/**
 * Global Toast Notification System
 * Provides useToast hook + <ToastContainer /> for app-wide in-app alerts.
 * Replaces native browser alert() everywhere.
 */
import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useTheme } from '../../context/ThemeContext';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastItem {
  id: number;
  msg: string;
  type: ToastType;
}

interface ToastContextValue {
  showToast: (msg: string, type?: ToastType) => void;
}

const ToastContext = createContext<ToastContextValue>({ showToast: () => {} });

let _counter = 0;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timers = useRef<Record<number, ReturnType<typeof setTimeout>>>({});
  const { theme } = useTheme();
  const isLight = theme === 'light';

  const dismiss = useCallback((id: number) => {
    clearTimeout(timers.current[id]);
    delete timers.current[id];
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const showToast = useCallback((msg: string, type: ToastType = 'info') => {
    const id = ++_counter;
    setToasts(prev => [...prev.slice(-4), { id, msg, type }]); // max 5 at once
    timers.current[id] = setTimeout(() => dismiss(id), 5000);
  }, [dismiss]);

  const bgFor = (type: ToastType) =>
    type === 'success'
      ? isLight ? 'rgba(240, 253, 244, 0.97)' : 'rgba(5, 68, 52, 0.94)'
      : type === 'error'
        ? isLight ? 'rgba(254, 242, 242, 0.97)' : 'rgba(127, 29, 29, 0.94)'
        : type === 'warning'
          ? isLight ? 'rgba(255, 251, 235, 0.97)' : 'rgba(120, 53, 15, 0.94)'
          : isLight ? 'rgba(239, 246, 255, 0.97)' : 'rgba(30, 58, 138, 0.94)';

  const borderFor = (type: ToastType) =>
    type === 'success' ? 'rgba(16, 185, 129, 0.5)'
      : type === 'error' ? 'rgba(239, 68, 68, 0.5)'
      : type === 'warning' ? 'rgba(245, 158, 11, 0.55)'
      : 'rgba(59, 130, 246, 0.5)';

  const colorFor = (type: ToastType) =>
    type === 'success' ? (isLight ? '#065f46' : '#6ee7b7')
      : type === 'error' ? (isLight ? '#991b1b' : '#fca5a5')
      : type === 'warning' ? (isLight ? '#92400e' : '#fcd34d')
      : (isLight ? '#1e40af' : '#93c5fd');

  const iconFor = (type: ToastType) =>
    type === 'success' ? '✅' : type === 'error' ? '❌' : type === 'warning' ? '⚠️' : 'ℹ️';

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}

      {/* Toast Stack — fixed top-right */}
      <div
        aria-live="assertive"
        style={{
          position: 'fixed',
          top: '18px',
          right: '22px',
          zIndex: 99999,
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          pointerEvents: 'none',
        }}
      >
        {toasts.map(t => (
          <div
            key={t.id}
            role="alert"
            style={{
              pointerEvents: 'all',
              display: 'flex',
              alignItems: 'flex-start',
              gap: '11px',
              minWidth: '300px',
              maxWidth: '460px',
              padding: '13px 15px',
              borderRadius: '12px',
              background: bgFor(t.type),
              border: `1px solid ${borderFor(t.type)}`,
              boxShadow: '0 12px 36px -4px rgba(0,0,0,0.32), 0 4px 10px rgba(0,0,0,0.18)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              animation: 'slideInRight 0.22s cubic-bezier(0.34,1.56,0.64,1)',
              color: colorFor(t.type),
            }}
          >
            {/* Icon */}
            <span style={{ fontSize: '1.1rem', flexShrink: 0, marginTop: '1px' }}>
              {iconFor(t.type)}
            </span>

            {/* Message */}
            <span style={{
              flex: 1,
              fontSize: '0.83rem',
              fontWeight: 500,
              lineHeight: 1.48,
            }}>
              {t.msg}
            </span>

            {/* Dismiss × */}
            <button
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss notification"
              style={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '22px',
                height: '22px',
                borderRadius: '5px',
                border: 'none',
                background: 'transparent',
                cursor: 'pointer',
                fontSize: '0.88rem',
                color: colorFor(t.type),
                opacity: 0.65,
                transition: 'opacity 0.15s, background 0.15s',
                padding: 0,
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLButtonElement).style.opacity = '1';
                (e.currentTarget as HTMLButtonElement).style.background = 'rgba(0,0,0,0.07)';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLButtonElement).style.opacity = '0.65';
                (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
              }}
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => useContext(ToastContext);
export const ToastContainer: React.FC = () => null;
